import React, { useState } from "react";

/**
 * Specialized NumberInput component for "afwerking" (finishing) operations
 * Handles synchronized updates across multiple related properties
 * Features validation with error display and conditional API requests
 *
 * @param {Object} props - Component props
 * @param {Object} props.field - Field configuration with validation rules (min, max, unit)
 * @param {Object} props.properties - Current form properties/values
 * @param {string} props.property - The primary property key this input manages
 * @param {Function} props.updateProperties - Callback to update parent component properties
 * @param {number} props.step - Step value for number increment/decrement (default: 1)
 */
const NumberInputFinishingAll = ({
    field,
    properties,
    property,
    updateProperties,
    step = 1,
}) => {

    // State for debouncing timeout and validation error display
    const [typingTimeout, setTimingTimeout] = useState(0);
    const [error, setError] = useState(false);

    /**
     * Handle input value changes with complex multi-property updates
     * Updates multiple related properties based on the primary property being changed
     * Implements validation and conditional API requests
     *
     * @param {Event} e - The input change event
     */
    function updateValue(e) {
        // Clone properties and parse input value (empty string if no value)
        let props = { ...properties };
        let intvalue = e.target.value ? parseInt(e.target.value) : "";
        props[property] = intvalue;

        // Handle synchronized updates for "sponning_rondom" (groove around) properties
        if (property === "sponning_rondom_breedte") {

            // Update all width-related groove properties
            props[getVariable("BO_BR")] = intvalue; // Top width
            props[getVariable("LI_BR")] = intvalue; // Left width
            props[getVariable("RE_BR")] = intvalue; // Right width
            props[getVariable("O_BR")] = intvalue;  // Bottom width

        } else if (property === "sponning_rondom_hoogte") {

            // Update all depth-related groove properties
            props[getVariable("BO_DI")] = intvalue; // Top depth
            props[getVariable("LI_DI")] = intvalue; // Left depth
            props[getVariable("RE_DI")] = intvalue; // Right depth
            props[getVariable("O_DI")] = intvalue;  // Bottom depth

        }

        // Update properties immediately without side effects
        updateProperties(props, false);

        // Validate input and determine if API request should be made
        let doRequest = intvalue < field.min || intvalue > field.max;
        setError(intvalue < field.min || intvalue > field.max);

        // Clear existing timeout for debouncing
        if (typingTimeout) {
            clearTimeout(typingTimeout);
        }

        // Set debounced update timeout - only make API request if validation passes
        setTimingTimeout(
            setTimeout(() => {
                if (doRequest === false) {
                    updateProperties(props); // Trigger API request only for valid values
                }
            }, 500),
        );
    }

    /**
     * Generate variable name for groove properties
     * Combines the groove type with the specific measurement type
     *
     * @param {string} variable - The measurement type (e.g., "BO_BR", "LI_DI")
     * @returns {string} The complete property name
     */
    const getVariable = (variable) => {
        return properties["sponning_rondom"] + "_" + variable;
    };

    /**
     * Handle input focus by selecting all text
     * Improves user experience for quick value replacement
     *
     * @param {Event} event - The focus event
     */
    function handleFocus(event) {
        if (event) {
            event.target.select();
        }
    }

    return (
        <div className={"row forminput"}>
            <div className="col-sm-12 needs-validation">
                <div className="input-group input-group-number has-validation">
                    <input
                        type="number"
                        className={`${error ? "is-invalid" : ""} form-control`}
                        name={property}
                        onChange={updateValue}
                        onFocus={handleFocus}
                        step={step}
                        value={properties[property] || ""}
                        min={field.min}
                        max={field.max}
                    />
                    {field.unit ? (
                        <div className="input-group-append">
                            <span className="input-group-text">
                                {field.unit}
                            </span>
                        </div>
                    ) : (
                        <></>
                    )}
                    <div className="invalid-feedback">
                        Waarde moet minimaal 1 zijn
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NumberInputFinishingAll;
